import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation } from "react-query";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";

//Components
import AuthenticateLayout from "../../../components/AuthenticateLayout";
import SelectCountry from "./SelectCountry";
import ConfirmPhoneNumber from "./ConfirmPhoneNumber";
import ChangeLanguage from "../../../components/ChangeLanguage";
import { Loading } from "../../../components";
import { Footer } from "../../../components/Footer";
import StepsPoint from "../../../components/StepsPoint";
import { PhoneNumberInput } from "./components/PhoneNumberInput";
import { AppSubmitButton } from "./components/AppSubmitButton";

//Services
import { validateOuPayment } from "../../../services/ads/ads.service";
import { postRegistration } from "../../../services/auth/auth.service";

// Constants & Utils
import { APP_NAME, LOGIN_METHOD } from "../../../constants";
import { getValueByLanguage, renderPhoneNumber } from "../../../utils/utils";
import { links } from "../../../constants/links";
import { ENV } from "../../../configs/env";

import {
  isLineMethod,
  isLineSessionMethod,
  isWhatsappMethod,
  LINE_LIFF_METHODS,
  LINE_NO_PHONE_METHODS,
} from "./components/loginMethods";

import { PlatformType } from "../../../services/auth/auth.interface";

// Hooks
import { useLoginParams } from "./hooks/useLoginParams";
import { usePhoneInput } from "./hooks/usePhoneInput";
import { useTnC } from "../../../hooks/useTnC";
import { useCustomHeadStyles } from "../../../hooks/useCustomHeadStyles";
import { lineSsoSubmit } from "./hooks/lineSsoSubmit";
import { lineLiffSubmit } from "./hooks/lineLiffSubmit";
import { useSubcampaign } from "./hooks/useSubCampaign";

// Styles
import { Wrapper, SubcampaignInvalidTextWrapper } from "./components/styles";

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const lineCallbackKeyRef = useRef<string | null>(null);
  const { i18n, t } = useTranslation();

  const defaultCountry: string =
    window.localStorage.getItem("country") ||
    process.env.REACT_APP_DEFAULT_COUNTRY ||
    "hk";
  // const defaultApplication: string = process.env.REACT_APP_DEFAULT_APPLICATION ?? LOGIN_METHOD.WHATSAPP;

  const merchant_reference = localStorage.getItem("merchant_reference") || "";
  const existingPlatformType = localStorage.getItem("last_platform_type");

  /* ---------------- Hooks ---------------- */
  const { accepted, setAccepted } = useTnC(false);
  const { mutate } = useMutation(postRegistration);
  const {
    subCampaignId,
    qrscanlogId,
    engageGetCreditSession,
    channelSessionId,
    isEGC,
    merchantReference,
  } = useLoginParams();

  const {
    country,
    displayValue,
    actualPhoneValue,
    existingPhoneNumber,
    handleOnChange,
    handleSelectCountry,
  } = usePhoneInput(defaultCountry);

  const {
    data,
    isLoading: isSubcampaignLoading,
    isSubcampaignValid,
    defaultLoginMethod,
  } = useSubcampaign({ subCampaignId, qrscanlogId });

  useCustomHeadStyles(data?.custom_html);

  /* ----------------- State ----------------- */
  const [showSelectCountry, setShowSelectCountry] = useState(false);
  const [loadingRegister, setLoadingRegister] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [appName, setAppName] = useState("");
  const [userID, setUserID] = useState<string | null>(() =>
    localStorage.getItem("userID"),
  );
  const [profileName, setProfileName] = useState<string | null>(() =>
    localStorage.getItem("profileName"),
  );

  /* ---------------- Derived ---------------- */
  const prefix = "+" + country[3];
  const phoneNumber = actualPhoneValue;
  const showLoading = isLoading || loadingRegister || isSubcampaignLoading;

  const steps = useMemo(() => {
    const value = getValueByLanguage(data, "highlighted_terms");
    return value ? JSON.parse(value) : [];
  }, [data, i18n.language]);

  const rewardSummaryImage = getValueByLanguage(data, "reward_summary_image");
  const bannerTopperImage = getValueByLanguage(
    data,
    "login_page_banner_topper_image",
  );
  const customHtml = getValueByLanguage(data, "custom_html_login");
  const pageAttentionTooltipTitle = getValueByLanguage(
    data,
    "login_page_attention_tooltip_title",
  );

  /* ----------------- Helpers ----------------- */
  const resolveAppName = (method: string) => {
    if (isLineMethod(method)) return APP_NAME.LINE;
    if (isWhatsappMethod(method)) return APP_NAME.WHATSAPP;
    if (method === LOGIN_METHOD.SMS) return APP_NAME.SMS;
    return "";
  };

  const getPrefix = () =>
    LINE_NO_PHONE_METHODS.includes(defaultLoginMethod) ? "" : prefix;

  const prefixValue = getPrefix();

  const getPhoneNumber = () =>
    LINE_NO_PHONE_METHODS.includes(defaultLoginMethod) ? "" : phoneNumber;

  const resolvePhoneNumber = () => {
    if (!existingPhoneNumber.current) return getPhoneNumber();
    const numericPrevix = prefixValue.replace("+", "");
    return existingPhoneNumber.current.slice(numericPrevix.length);
  };

  /* ----------------- Effects ----------------- */
  useEffect(() => {
    const isLineCallback = localStorage.getItem("LINE_AUTH_CALLBACK");
    const lineUserID = localStorage.getItem("userID");
    const lineProfileName = localStorage.getItem("profileName");

    if (
      isLineCallback &&
      LINE_LIFF_METHODS.includes(defaultLoginMethod) &&
      lineUserID &&
      lineUserID !== "undefined"
    ) {
      setIsLoading(true);
      setUserID(lineUserID);
      setProfileName(lineProfileName);
      localStorage.removeItem("LINE_AUTH_CALLBACK");
      setTimeout(() => {
        handleSubmit(defaultLoginMethod);
      }, 300);
    }
  }, [defaultLoginMethod]);

  // Handle LINE SSO Callback
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const code = searchParams.get("code");
    const state = searchParams.get("state");

    if (!code || !state) return;

    // When /login loads after a LIFF → LINE SSO redirect, there may be no subcampaign_id
    // in the URL, so defaultLoginMethod won't be set yet. Fall back to the login_method
    // we stored in localStorage inside LineLiffAuth before triggering the SSO redirect.
    const liffRestoreMethod = localStorage.getItem("login_method") || "";
    const effectiveLoginMethod = defaultLoginMethod || liffRestoreMethod;

    if (!effectiveLoginMethod) return;

    if (
      code &&
      state &&
      (LINE_LIFF_METHODS.includes(effectiveLoginMethod) ||
        defaultLoginMethod === LOGIN_METHOD.LINE_SSO)
    ) {
      const callbackKey = `${code}:${state}`;
      const storedCallbackKey = sessionStorage.getItem("LINE_SSO_CALLBACK_KEY");

      if (
        lineCallbackKeyRef.current === callbackKey ||
        storedCallbackKey === callbackKey
      ) {
        return;
      }

      lineCallbackKeyRef.current = callbackKey;
      sessionStorage.setItem("LINE_SSO_CALLBACK_KEY", callbackKey);

      const handleSsoCallback = async () => {
        setIsLoading(true);
        setError("");
        try {
          const LineLoginModule = await import("./sso/line-login");
          const LineLogin = LineLoginModule.default;
          const lineLogin = new LineLogin(effectiveLoginMethod);

          const result = await lineLogin.callback(code, state);
          // Handle the iOS/Universal Link fallback case
          if (result && result.actionRequired === "add_friend") {
            setIsLoading(false);
            window.location.href = result.redirectUrl;
            return;
          }

          if (result && result.actionRequired === "success") {
            const payload = result.payload;

            if (payload && payload.sub) {
              localStorage.setItem("userID", payload.sub);
              localStorage.setItem("profileName", payload.name || "");
              setUserID(payload.sub);
              setProfileName(payload.name || "");

              // Clean up LIFF SSO restore keys
              localStorage.removeItem("login_method");
              localStorage.removeItem("LIFF_LOGIN_CONTEXT");

              // Clean query parameters from URL
              const newSearch = new URLSearchParams(location.search);
              newSearch.delete("code");
              newSearch.delete("state");
              const newSearchStr = newSearch.toString();
              navigate(
                {
                  pathname: location.pathname,
                  search: newSearchStr ? `?${newSearchStr}` : "",
                },
                { replace: true },
              );

              setTimeout(() => {
                handleSubmit(effectiveLoginMethod);
              }, 300);
            } else {
              setError(t("lineLoginFailed") || "Line login failed");
              setIsLoading(false);
            }
          }
        } catch (err: any) {
          console.error("LINE SSO Callback Error:", err);
          setError(err.message || t("lineLoginFailed") || "Line login failed");
          setIsLoading(false);
        }
      };

      handleSsoCallback();
    }
  }, [location.search, defaultLoginMethod, navigate, t]);

  //pay_get_coupon
  useEffect(() => {
    if (merchant_reference && subCampaignId && data?.is_pay_get_coupon) {
      setIsLoading(true);
      validateOuPayment({ merchant_reference, subCampaignId })
        .then((res: any) => {
          setIsLoading(false);
        })
        .catch((err: Error) => {
          setIsLoading(false);
        });
    }
  }, [merchant_reference, subCampaignId, data?.is_pay_get_coupon]);

  useEffect(() => {
    if (!defaultLoginMethod) return;
    const resolved = resolveAppName(defaultLoginMethod);
    if (!resolved) {
      setError(t("appNameNotFound"));
    }
    setAppName(resolved);
  }, [data, defaultLoginMethod, t]);

  const handleLineSsoSubmit = () => {
    lineSsoSubmit({
      defaultLoginMethod,
      subCampaignId,
      qrscanlogId,
      channelSessionId,
      engageGetCreditSession,
      isEGC,
      merchantReference,
      handleSubmit,
    });
  };

  const handleLineLiffSubmit = () => {
    lineLiffSubmit({
      defaultLoginMethod,
      subCampaignId,
      qrscanlogId,
      channelSessionId,
      engageGetCreditSession,
      isEGC,
      merchantReference,
      advertiserLogo: data?.advertiser_logo || "",
      handleSubmit,
    });
  };

  const handleSubmit = (platformOverride?: string) => {
    setLoadingRegister(true);

    const phoneNumberValue = resolvePhoneNumber();
    const advertiserLogo = data?.advertiser_logo || localStorage.getItem("advertiser_logo") || "";

    mutate(
      {
        prefix: prefixValue,
        phoneNumber: phoneNumberValue,
        platform: (platformOverride || defaultLoginMethod) as PlatformType,
        subCampaignId: subCampaignId || "0",
        qrscanlog_id: qrscanlogId,
        engage_get_credit_session: engageGetCreditSession,
        lineUserID: userID || localStorage.getItem("userID") || "",
        is_pay_get_coupon: Boolean(data?.is_pay_get_coupon),
        merchant_reference:
          data?.is_pay_get_coupon && merchant_reference
            ? merchant_reference
            : "",
        profileName: profileName || localStorage.getItem("profileName") || "",
        channelSessionId: channelSessionId,
      },
      {
        onSuccess: (data: object) => {
          const userId4GA = phoneNumberValue
            ? prefixValue + phoneNumberValue
            : userID;

          if (
            typeof window.gtag === "function" &&
            typeof window.GA_CONFIG === "object" &&
            window.GA_CONFIG.measurementId &&
            userId4GA
          ) {
            const userId = `${window.GA_CONFIG.params.site_name}-${userId4GA}`;

            window.GA_CONFIG.params = {
              ...window.GA_CONFIG.params,
              login_id: userId,
              user_id: userId,
            };

            window.localStorage.setItem(
              "GA_CONFIG",
              JSON.stringify(window.GA_CONFIG),
            );

            // Calling gtag("set") or gtag("config") here will not set the login_id as event param.
          }

          setLoadingRegister(false);
          navigate(links.RESEND, {
            state: {
              phoneNumberValue,
              prefixValue,
              appName,
              data,
              defaultLoginMethod,
              advertiserLogo,
            },
          });
        },
        onError: (error) => {
          setLoadingRegister(false);
          if (data?.is_engage_get_credit) {
            navigate(`${links.CAMPAIGN_STATUS.replace(":status", "error")}`, {
              state: {
                background: data?.background,
                banner_image_corner: data?.banner_image_corner,
              },
            });
          } else {
            setError(
              t("thePhoneNumber") +
              renderPhoneNumber(prefix, phoneNumber) +
              t("isNotRegisterWhatsApp"),
            );
          }
        },
      },
    );
  };

  const disableButton =
    ((!existingPhoneNumber.current ||
      defaultLoginMethod !== existingPlatformType ||
      !data?.is_pay_get_coupon) &&
      (!actualPhoneValue || !displayValue || !country)) ||
    !accepted;

  if (isSubcampaignLoading) {
    return <Loading />;
  }

  return (
    <>
      {showLoading && <Loading />}
      {error ? (
        <ConfirmPhoneNumber
          setIsConfirm={false}
          phoneNumber={actualPhoneValue}
          prefix={"+" + country[3]}
          appName={appName}
          data={data}
          isError={error}
          qrscanlog_id={qrscanlogId}
          defaultLoginMethod={defaultLoginMethod}
        />
      ) : showSelectCountry ? (
        <SelectCountry
          setShowSelectCountry={setShowSelectCountry}
          setCountry={handleSelectCountry}
        />
      ) : (
        <Wrapper>
          <AuthenticateLayout
            button={() => !ENV.HIDE_TRANSLATOR && <ChangeLanguage />}
            isLoading={showLoading}
            background={data?.background || data?.advertiser_banner}
            bannerTopperImage={!customHtml && bannerTopperImage}
            bannerImageCorner={data?.banner_image_corner}
            defaultBackground={""}
            hideLogo={ENV.HIDE_LOGO}
          >
            {customHtml && (
              <div
                style={{ width: "100%", padding: "0px 0px 0px 0px" }}
                className="ql-editor mobile-preview-content"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(customHtml),
                }}
              />
            )}
            {isSubcampaignValid ? (
              rewardSummaryImage ? (
                <img src={rewardSummaryImage} alt="icon" className="banner" />
              ) : null
            ) : null}
            {/* --- ADD BOT FALLBACK UI OR NORMAL FORM --- */}
            {isSubcampaignValid ? (
              <>
                <div style={{ height: 28 }} />
                <div style={{ position: "sticky", bottom: 20 }}>
                  {LINE_NO_PHONE_METHODS.includes(defaultLoginMethod) ? null : (
                    <PhoneNumberInput
                      country={country}
                      value={
                        defaultLoginMethod === existingPlatformType &&
                          existingPhoneNumber.current &&
                          data?.is_pay_get_coupon
                          ? existingPhoneNumber.current
                          : displayValue
                      }
                      onChange={handleOnChange}
                      onSelectCountry={() => setShowSelectCountry(true)}
                      placeholder={t("yourPhoneNumber")}
                    />
                  )}
                  {
                    <AppSubmitButton
                      appName={defaultLoginMethod}
                      pageAttentionTooltipTitle={
                        // Don't show tooltip as it covered phone number input
                        isWhatsappMethod(defaultLoginMethod)
                          ? ""
                          : pageAttentionTooltipTitle
                      }
                      isEGC={isEGC}
                      disableButton={disableButton}
                      accepted={accepted}
                      handleSubmit={handleSubmit}
                      handleSsoSubmit={handleLineSsoSubmit}
                      handleLiffSubmit={handleLineLiffSubmit}
                    />
                  }
                </div>
                <div style={{ margin: "15px 0 45px" }}>
                  <StepsPoint steps={steps} textTopMargin={-13} />
                </div>
              </>
            ) : (
              <div
                className="subcampaign_invalid"
                style={{ width: "100%", height: "100%", marginTop: "30%" }}
              >
                <SubcampaignInvalidTextWrapper>
                  {t("campaignIsnotAvailable")}
                </SubcampaignInvalidTextWrapper>
              </div>
            )}
            {isSubcampaignValid ? (
              <Footer
                showCheckbox
                tncChecked={accepted}
                setTncChecked={setAccepted}
              />
            ) : null}
          </AuthenticateLayout>
        </Wrapper>
      )}
    </>
  );
};

export default Login;
